import { useState, useEffect, useCallback } from 'react';
import { callRpc } from '../../lib/rpcClient';
import { RotateCcw, MessageSquare, Clock, User, AlertCircle, Edit3, Save } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { AlertModal } from '../../components/ui/AlertModal';

type SubmissionStatus = 'new' | 'in_progress' | 'resolved' | 'dismissed';

interface ContactSubmission {
  id: string;
  user_id: string | null;
  user_email: string | null;
  title: string;
  message: string;
  status: SubmissionStatus;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
}

export function AdminSupport() {
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editNotes, setEditNotes] = useState<string>('');
  const [editStatus, setEditStatus] = useState<SubmissionStatus>('new');
  const [alertConfig, setAlertConfig] = useState<{ 
      isOpen: boolean; 
      title: string; 
      message: string;
      confirmLabel?: string;
  }>({ isOpen: false, title: '', message: '' });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await callRpc('get_admin_contact_submissions', {
        p_limit: 50,
        p_offset: 0,
        p_status: filter
      });
      setSubmissions(result.submissions);
    } catch (err: any) {
      console.error('Failed to load support requests:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleUpdate = async (id: string, status: SubmissionStatus, notes: string) => {
    try {
      await callRpc('update_admin_contact_submission', {
        p_id: id,
        p_status: status,
        p_admin_notes: notes
      });
      setEditingId(null);
      loadData();
    } catch (err: any) {
      setAlertConfig({
        isOpen: true,
        title: 'Error',
        message: err.message || 'Failed to update submission',
        confirmLabel: 'Labi'
      });
    }
  };

  const statusColors: Record<SubmissionStatus, string> = {
    new: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
    in_progress: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
    resolved: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
    dismissed: 'text-slate-500 bg-slate-500/10 border-slate-500/20'
  };

  return (
    <div className="space-y-8 font-primary pb-12">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-white mb-1">Support Requests</h1>
          <p className="text-slate-400">Manage contact form submissions and user questions.</p>
        </div>
        <button 
          onClick={() => loadData()}
          disabled={loading}
          className="p-3 border border-slate-800 bg-slate-900 rounded-2xl hover:bg-slate-800 disabled:opacity-30 transition-all shadow-sm active:scale-95 text-slate-500 hover:text-blue-400 group"
        >
          <RotateCcw size={20} className={loading ? 'animate-spin' : 'group-hover:rotate-[-30deg] transition-transform'} />
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none">
        {['all', 'new', 'in_progress', 'resolved', 'dismissed'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all border ${
              filter === f 
                ? 'bg-blue-600 border-blue-500 text-white shadow-lg shadow-blue-900/20' 
                : 'bg-slate-900 border-slate-800 text-slate-500 hover:border-slate-700'
            }`}
          >
            {f.replace('_', ' ')}
          </button>
        ))}
      </div>

      {loading && submissions.length === 0 ? (
        <div className="flex justify-center p-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : submissions.length === 0 ? (
        <div className="bg-slate-900/50 border border-slate-800 rounded-3xl p-12 text-center">
          <MessageSquare size={48} className="mx-auto text-slate-700 mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">No support requests found</h3>
          <p className="text-slate-500">Everything is quiet for now!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {submissions.map((sub) => (
            <div 
              key={sub.id} 
              className={`bg-slate-900 border rounded-3xl p-6 transition-all hover:border-slate-700 ${
                sub.status === 'new' ? 'border-blue-500/30 ring-1 ring-blue-500/10' : 'border-slate-800'
              }`}
            >
              <div className="flex flex-col md:flex-row justify-between gap-4 mb-4">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-2xl ${sub.status === 'new' ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-800 text-slate-500'}`}>
                    <MessageSquare size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-white leading-tight mb-1">{sub.title}</h3>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                      <div className="flex items-center gap-1">
                        <User size={14} />
                        <span className="font-bold">{sub.user_email || 'Anonymous'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock size={14} />
                        <span>{formatDistanceToNow(new Date(sub.created_at), { addSuffix: true })}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${statusColors[sub.status]}`}>
                    {sub.status.replace('_', ' ')}
                  </span>
                </div>
              </div>

              <div className="bg-slate-950/50 rounded-2xl p-4 mb-4 border border-slate-800/50">
                <p className="text-slate-300 whitespace-pre-wrap leading-relaxed">{sub.message}</p>
              </div>

              {editingId === sub.id ? (
                <div className="space-y-4 bg-slate-800/50 p-4 rounded-2xl border border-blue-500/20">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 px-1">Update Status</label>
                      <select 
                        value={editStatus}
                        onChange={(e) => setEditStatus(e.target.value as SubmissionStatus)}
                        className="w-full bg-slate-900 border border-slate-700 text-white rounded-xl px-4 py-2.5 outline-none focus:border-blue-500 transition-all font-bold text-sm"
                      >
                        <option value="new">New</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="dismissed">Dismissed</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 px-1">Admin Notes</label>
                      <textarea
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        placeholder="Add internal notes..."
                        className="w-full bg-slate-900 border border-slate-700 text-white rounded-xl px-4 py-2.5 outline-none focus:border-blue-500 transition-all font-bold text-sm h-24"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button 
                      onClick={() => setEditingId(null)}
                      className="px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest text-slate-500 hover:text-white transition-colors"
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={() => handleUpdate(sub.id, editStatus, editNotes)}
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest shadow-lg shadow-blue-500/20 transition-all active:scale-95"
                    >
                      <Save size={14} />
                      Save Changes
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div className="flex-1 w-full">
                    {sub.admin_notes && (
                      <div className="flex items-start gap-2 text-xs text-slate-500 italic bg-slate-950/30 p-2 rounded-lg border border-slate-800/30">
                        <AlertCircle size={12} className="shrink-0 mt-0.5" />
                        <span>Notes: {sub.admin_notes}</span>
                      </div>
                    )}
                  </div>
                  <button 
                    onClick={() => {
                      setEditingId(sub.id);
                      setEditNotes(sub.admin_notes || '');
                      setEditStatus(sub.status);
                    }}
                    className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95"
                  >
                    <Edit3 size={14} />
                    Manage
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <AlertModal
        {...alertConfig}
        onClose={() => setAlertConfig(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}
